import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api } from "../../api";
import { PAGE_SIZE_ERRORS_EVENTS } from "../../constants";
import type {
  ErrorRecord,
  ErrorSummaryRow,
  PaginatedErrors,
} from "../../types/errors";

interface ErrorsState {
  summary: ErrorSummaryRow[];
  events: ErrorRecord[];
  eventsTotal: number;
  eventsPage: number;
  eventsLimit: number;
  eventsTotalPages: number;
  selectedAllianceId: string | null;
  summaryLoading: boolean;
  eventsLoading: boolean;
  error: string | null;
}

const initialState: ErrorsState = {
  summary: [],
  events: [],
  eventsTotal: 0,
  eventsPage: 1,
  eventsLimit: PAGE_SIZE_ERRORS_EVENTS,
  eventsTotalPages: 0,
  selectedAllianceId: null,
  summaryLoading: false,
  eventsLoading: false,
  error: null,
};

export const fetchErrorsSummary = createAsyncThunk(
  "errors/fetchSummary",
  async () => api.getErrorsSummary() as Promise<ErrorSummaryRow[]>,
);

export const SYSTEM_ALLIANCE_ID = "__system__";

export const fetchErrors = createAsyncThunk(
  "errors/fetchEvents",
  async (params: {
    allianceId?: string | null;
    page?: number;
    limit?: number;
  }) => {
    const isSystem = params.allianceId === SYSTEM_ALLIANCE_ID;
    const response = (await api.getErrors({
      ...(isSystem
        ? { systemOnly: true }
        : params.allianceId
          ? { allianceId: params.allianceId }
          : {}),
      page: params.page ?? 1,
      limit: params.limit ?? PAGE_SIZE_ERRORS_EVENTS,
    })) as PaginatedErrors;
    return response;
  },
);

const errorsSlice = createSlice({
  name: "errors",
  initialState,
  reducers: {
    setSelectedAllianceId: (state, action) => {
      state.selectedAllianceId = action.payload;
      state.eventsPage = 1;
    },
    setEventsPage: (state, action: PayloadAction<number>) => {
      state.eventsPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchErrorsSummary.pending, (state) => {
        state.summaryLoading = true;
        state.error = null;
      })
      .addCase(fetchErrorsSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchErrorsSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.error.message || "Greška pri dohvaćanju sažetka";
      })
      .addCase(fetchErrors.pending, (state) => {
        state.eventsLoading = true;
        state.error = null;
      })
      .addCase(fetchErrors.fulfilled, (state, action) => {
        state.eventsLoading = false;
        state.events = action.payload.data;
        state.eventsTotal = action.payload.total;
        state.eventsPage = action.payload.page;
        state.eventsLimit = action.payload.limit;
        state.eventsTotalPages = action.payload.totalPages;
      })
      .addCase(fetchErrors.rejected, (state, action) => {
        state.eventsLoading = false;
        state.error = action.error.message || "Greška pri dohvaćanju grešaka";
      });
  },
});

export const { setSelectedAllianceId, setEventsPage } = errorsSlice.actions;
export const errorsReducer = errorsSlice.reducer;
